'use strict';

import barba from '@barba/core';
import {gsap, ScrollTrigger} from 'tc-neos-animations';
import customCursor from './custom-cursor.mjs';
import menu from './menu.mjs';
import utilities from '../utility/utilities.js';
import animationsUtility from '../utility/animations.js';
import logo from './logo.mjs';
import newsletterBloc from './newsletter-bloc.mjs';
import footerToTop from './footer-to-top.mjs';
import scrollingKeywords from './scrolling-keywords.mjs';
import scrollingCustomers from './scrolling-customers.mjs';
import headingAnimation from './heading-animation.mjs';
// Use global tcNeosElementsBootstrap (exposed by main index.js)
const tcNeosElementsBootstrap = window.tcNeosElementsBootstrap;
import servicesList from './services-list.mjs';
import projectsList from './projects-list.mjs';
import projectHeader from './project-header.mjs';
import projectTags from './project-tags.mjs';
import projectContentGrid from './project-content-grid.mjs';
import projectPagination from './project-pagination.mjs';
import collaboratorsGrid from './collaborators-grid.mjs';
import contactForm from './contact-form.mjs';
import socialShare from './social-share.mjs';
import blogArticlesList from './blog-articles-list.mjs';
import videoPlayer from './video-player.mjs';
import mailtoInterceptor from './mailto-interceptor.mjs';
import alpineManager from './alpine-manager.mjs';

let firstPageLoad = true;
let anchorHandlerBound = false;
let viewportObserver = null;

/**
 * Détecte une URL vers contact ou page de confirmation (toutes langues).
 * Ces pages doivent être chargées via un vrai GET (navigateur), jamais via Barba :
 * contact (Turnstile, finishers) et confirmation (redirection post-formulaire).
 */
const isFullPageGetHref = (href) => {
    if (!href) return false;
    try {
        const url = new URL(href, window.location.origin);
        if (url.origin !== window.location.origin) return false;
        // /contact, /fr/contact, /fr/contact/confirmation, /fr/merci, etc.
        return (
            /^\/(?:[a-z]{2}\/)?contact(?:\/confirmation)?\/?$/i.test(url.pathname) ||
            /^\/(?:[a-z]{2}\/)?merci\/?$/i.test(url.pathname)
        );
    } catch {
        return false;
    }
};

const shouldPrefetch = (href) => {
    if (!href) return false;
    if (href.startsWith('#')) return false;
    if (href.startsWith('tel:')) return false;
    if (href.startsWith('mailto:')) return false;
    if (href.startsWith('javascript:')) return false;
    // Ne pas précharger contact / confirmation : servies en vrai GET (hors Barba).
    if (isFullPageGetHref(href)) return false;
    try {
        const url = new URL(href, window.location.origin);
        // only same-origin http(s)
        if (url.origin !== window.location.origin) return false;
        return true;
    } catch {
        return false;
    }
};

const prefetch = (href) => {
    try {
        const url = new URL(href, window.location.origin);
        const key = url.href;

        // already cached? skip
        if (barba.cache.has(key)) return;

        fetch(key, {credentials: 'same-origin'})
            .then(res => res.text())
            .then(html => {
                const doc = barba.dom.toDocument(html);
                const container = doc.querySelector('[data-barba="container"]');
                if (!container) return;

                barba.cache.set(key, {
                    html,
                    url: key,
                    container,
                    namespace: container.getAttribute('data-barba-namespace')
                });
            })
            .catch(() => {
            });
    } catch {
        // ignore malformed URLs
    }
};

// delegated hover/touch prefetch (desktop + mobile)
const bindHoverTouchPrefetch = () => {
    // remove previous handlers if any
    document.removeEventListener('mouseenter', __hoverHandler, true);
    document.removeEventListener('touchstart', __touchHandler, true);

    function closestAnchor(el) {
        while (el && el !== document && !(el instanceof HTMLAnchorElement)) {
            el = el.parentElement;
        }
        return el instanceof HTMLAnchorElement ? el : null;
    }

    function __hoverHandler(e) {
        const a = closestAnchor(e.target);
        if (!a) return;
        const href = a.getAttribute('href');
        if (!shouldPrefetch(href)) return;
        prefetch(href);
    }

    function __touchHandler(e) {
        const a = closestAnchor(e.target);
        if (!a) return;
        const href = a.getAttribute('href');
        if (!shouldPrefetch(href)) return;
        prefetch(href);
    }

    // store refs on window so we can remove later if needed
    window.__tcHoverPrefetch = __hoverHandler;
    window.__tcTouchPrefetch = __touchHandler;

    document.addEventListener('mouseenter', __hoverHandler, true);
    document.addEventListener('touchstart', __touchHandler, {passive: true, capture: true});
};

// viewport prefetch (quicklink-like) with hard resets between pages
const enableViewportPrefetch = () => {
    // disconnect old observer (prevents stale targets after a swap)
    if (viewportObserver) {
        viewportObserver.disconnect();
        viewportObserver = null;
    }
    if (!('IntersectionObserver' in window)) return;

    viewportObserver = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                const link = entry.target;
                if (!(link instanceof HTMLAnchorElement)) {
                    viewportObserver.unobserve(link);
                    return;
                }
                const href = link.getAttribute('href');
                if (!shouldPrefetch(href)) {
                    viewportObserver.unobserve(link);
                    return;
                }
                prefetch(href);
                viewportObserver.unobserve(link);
            });
        },
        {rootMargin: '200px', threshold: 0.1}
    );

    // observe current anchors
    document.querySelectorAll('a[href]').forEach((a) => {
        const href = a.getAttribute('href');
        if (!shouldPrefetch(href)) return;
        viewportObserver.observe(a);
    });
};

// helper to (re)bind prefetchers after any DOM change
const rebindPrefetchers = () => {
    bindHoverTouchPrefetch();
    enableViewportPrefetch();
};

// Handle scroll position based on navigation type
const handleScrollPosition = () => {
    const navigationType = sessionStorage.getItem('tc-navigation-type');
    const savedScrollPosition = sessionStorage.getItem('tc-scroll-position');
    const savedUrl = sessionStorage.getItem('tc-scroll-url');
    const currentUrl = window.location.href;

    // Détecter si on est sur mobile pour forcer le scroll natif
    const isMobile = window.innerWidth <= 768;

    if (navigationType === 'back' && savedScrollPosition && savedUrl === currentUrl) {
        // Restore scroll position for back navigation
        const scrollY = parseInt(savedScrollPosition, 10);
        if (scrollY > 0) {
            // Sur mobile, utiliser le scroll natif pour que la scrollbar se mette à jour
            if (isMobile || !window.lenis) {
                window.scrollTo(0, scrollY);
            } else {
                window.lenis.scrollTo(scrollY, {immediate: true});
            }
        }
    } else {
        // Scroll to top for forward navigation or new pages
        if (isMobile || !window.lenis) {
            window.scrollTo(0, 0);
        } else {
            window.lenis.scrollTo(0, {immediate: true});
        }
    }

    // Clean up session storage
    sessionStorage.removeItem('tc-scroll-position');
    sessionStorage.removeItem('tc-scroll-url');
    sessionStorage.removeItem('tc-navigation-type');
};

/**
 * Normalize hash from Barba navigation trigger or current location.
 */
const getHashFromTriggerOrLocation = (data) => {
    let hash = '';
    const el = data?.trigger;
    if (el && el.tagName === 'A') {
        const href = el.getAttribute('href') || '';
        try {
            const u = new URL(href, window.location.origin);
            hash = u.hash || '';
        } catch {
            if (href.startsWith('#')) hash = href;
        }
    }
    if (!hash) hash = window.location.hash || '';
    if (hash && !hash.startsWith('#')) hash = `#${hash}`;
    return hash;
};

/**
 * Ensure a single hashchange listener is attached (avoid duplicates across navigations).
 */
const ensureAnchorHandler = () => {
    if (anchorHandlerBound) return;
    const onHashChange = () => {
        if (tcNeosElementsBootstrap && tcNeosElementsBootstrap.anchor) {
            tcNeosElementsBootstrap.anchor();
        }
    };
    window.addEventListener('hashchange', onHashChange);
    anchorHandlerBound = true;
};

const updateHtmlTag = (data) => {
    if(typeof data.next.url === "string") {
        if (data.next.url.indexOf('/en') === -1) {
            document.documentElement.lang = 'fr'
        } else {
            document.documentElement.lang = 'en'
        }
    }
}

/**
 * Replace <head> metas/LD+JSON from next HTML safely.
 */
const updateHeadTag = (data) => {
    const nextHtml = data?.next?.html || '';
    const match = nextHtml.match(/<head[^>]*>([\s\S.]*)<\/head>/i);
    if (!match) return;

    const tempDom = document.createElement('div');
    tempDom.innerHTML = match[1];

    // Update title
    const nextTitle = tempDom.querySelector('title')?.innerText;
    if (nextTitle) document.title = nextTitle;

    // Remove metas and structured data
    document.head.querySelectorAll('meta, script[type="application/ld+json"]').forEach((tag) => tag.remove());

    // Inject new metas and ld+json
    tempDom.querySelectorAll('meta, script[type="application/ld+json"]').forEach((tag) => {
        document.head.appendChild(tag);
    });
};

/**
 * Replace body classes from the next HTML document.
 */
const updateBodyClasses = (nextHtml) => {
    const bodyClassMatch = nextHtml.match(/<body[^>]*class="([^"]*)"/);
    document.body.className = bodyClassMatch ? bodyClassMatch[1] : '';
};

/**
 * Reload a body script by ID (useful for page-specific bootstrap).
 */
const initBodyScript = (scriptId) => {
    const oldScript = document.getElementById(scriptId);
    if (!oldScript) return;
    const newScript = document.createElement('script');
    newScript.src = oldScript.src;
    newScript.id = scriptId;
    oldScript.parentNode.replaceChild(newScript, oldScript);
};

/**
 * Reload slipstreamed module scripts with cache-busting.
 */
const reloadJonnittoVideoScripts = () => {
    const scripts = document.querySelectorAll('script[type="module"][data-slipstream]');
    scripts.forEach((oldScript) => {
        const newScript = document.createElement('script');
        Array.from(oldScript.attributes).forEach((attr) => {
            newScript.setAttribute(attr.name, attr.value);
        });
        newScript.src = `${oldScript.src}?bust=${Date.now()}`;
        oldScript.parentNode.replaceChild(newScript, oldScript);
    });
};

/**
 * Reinitialize Alpine.js components after Barba transition
 */
const reinitializeAlpineComponents = () => {
    // Use our Alpine manager
    alpineManager.reinitialize();
};

/**
 * Trigger Google Tag Manager virtual page view event after Barba transition
 * This ensures GTM tracks page views for SPA navigation, behaving like a standard page load
 * All GA4 calls go through GTM via the custom trigger "virtual_pageview"
 */
const triggerGTMPageView = () => {
    // Wait a bit to ensure DOM is fully updated
    setTimeout(() => {
        const pageData = {
            url: window.location.href,
            path: window.location.pathname,
            title: document.title
        };

        // Push custom event to GTM dataLayer
        // This will trigger the "virtual_pageview" custom trigger in GTM
        if (typeof window.dataLayer !== 'undefined') {
            window.dataLayer.push({
                event: 'virtual_pageview',
                page: pageData
            });
        } else {
            // If dataLayer doesn't exist yet, try again after a short delay
            setTimeout(() => {
                if (typeof window.dataLayer !== 'undefined') {
                    window.dataLayer.push({
                        event: 'virtual_pageview',
                        page: pageData
                    });
                }
            }, 100);
        }
    }, 50);
};

/**
 * Init all page-specific modules (wrapped in try/catch).
 */
const initPageScripts = () =>
    new Promise((resolve) => {
        try {
            const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
            if (!isSafari) {
                // Kill ScrollTriggers from previous page
                ScrollTrigger.getAll().forEach((t) => t.kill());
            }

            // Init project-specific scripts
            utilities.init();
            customCursor.resetCursor();
            customCursor.init();
            logo.animateHeaderLogo();
            menu.navActiveShortcuts();
            menu.initMegaMenu();
            newsletterBloc.init();
            footerToTop.init();
            scrollingCustomers.init();
            scrollingKeywords.init();
            headingAnimation.init();
            servicesList.init();
            projectsList.init();
            projectHeader.init();
            projectTags.init();
            projectContentGrid.init();
            projectPagination.init();
            collaboratorsGrid.init();
            contactForm.init();
            socialShare.init();
            blogArticlesList.init();
            videoPlayer.init();
            mailtoInterceptor.init();

            if (tcNeosElementsBootstrap && tcNeosElementsBootstrap.anchor) {
                tcNeosElementsBootstrap.anchor();
            }
            ensureAnchorHandler();

            requestAnimationFrame(() => resolve());
        } catch (error) {
            console.error('Error in initPageScripts:', error);
            resolve();
        }
    });

/**
 * Main Barba init with lifecycle hooks.
 */
const init = () => {
    const transitionLogo = document.getElementById('transition-logo');
    let showLogoTimeout = null;
    let isTransitionInProgress = false;

    const transitions = [
        {
            name: 'fade',
            leave({current}) {
                // Marquer le début de la transition
                isTransitionInProgress = true;

                // Add black background to body during transition
                document.body.style.backgroundColor = '#000';

                // Only show logo for forward navigation (not back/forward)
                const navigationType = sessionStorage.getItem('tc-navigation-type');
                if (navigationType !== 'back') {
                    // Démarrer le timer de 300ms
                    showLogoTimeout = setTimeout(() => {
                        // Vérifier si la transition est encore en cours après 300ms
                        if (isTransitionInProgress) {
                            transitionLogo.classList.remove('hidden');
                        }
                    }, 300);
                }

                return gsap.to(current.container, {
                    opacity: 0,
                    duration: 0.3
                });
            },
            enter({next}) {
                clearTimeout(showLogoTimeout);
                document.body.classList.add('no-scroll');

                // Ensure next container is completely hidden immediately
                gsap.set(next.container, {
                    opacity: 0,
                    display: 'block',
                    visibility: 'hidden',
                    clearProps: 'display'
                });

                // Prevent any scroll during transition
                window.scrollTo(0, 0);

                setTimeout(() => {
                    transitionLogo.classList.add('hidden');
                }, 400);

                return Promise.resolve();
            }
        }
    ];

    // prevent auto scroll from browser on back/forward
    if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
    }

    // Detect back/forward navigation
    let isBackNavigation = false;
    window.addEventListener('popstate', () => {
        isBackNavigation = true;
        sessionStorage.setItem('tc-navigation-type', 'back');
    });

    ['projects-loaded', 'new-loaded-projects'].forEach((eventName) => {
        window.addEventListener(eventName, () => {
            const scrollPosition = sessionStorage.getItem('tc-projects-scroll');
            if (scrollPosition) {
                animationsUtility.scrollToPosition(scrollPosition);
                sessionStorage.removeItem('tc-projects-scroll');
            }

            setTimeout(() => {
                rebindPrefetchers();
            }, 50)
        });
    });

    // Prevent Barba from hijacking same-page anchors
    const prevent = ({el}) => {
        if (!el || el.tagName !== 'A') return false;
        const href = el.getAttribute('href') || '';
        if (!href) return false;

        // Forcer un vrai GET (sans Barba) vers contact ou confirmation.
        if (isFullPageGetHref(href)) return true;

        if (href.startsWith('#')) return true;
        try {
            const u = new URL(href, window.location.origin);
            if (u.hash && u.pathname === window.location.pathname) return true;
        } catch { /* noop */
        }

        return false;
    };

    // Initialize Barba
    barba.init({transitions, prevent});

    rebindPrefetchers();

    // Cleanup before leaving the page
    barba.hooks.beforeLeave((data) => {
        projectsList.destroy();

        // Clean up Alpine.js components
        alpineManager.destroy();

        // Clean up cursor observer
        customCursor.cleanupCursorObserver();

        // Save current scroll position for back/forward navigation
        const currentScrollY = window.scrollY;
        const currentUrl = window.location.href;
        sessionStorage.setItem('tc-scroll-position', currentScrollY.toString());
        sessionStorage.setItem('tc-scroll-url', currentUrl);

        // Mark this as a forward navigation (not back)
        sessionStorage.setItem('tc-navigation-type', 'forward');

        // optional hard clean (they will rebind after):
        document.removeEventListener('mouseenter', window.__tcHoverPrefetch, true);
        document.removeEventListener('touchstart', window.__tcTouchPrefetch, true);
        if (viewportObserver) {
            viewportObserver.disconnect();
            viewportObserver = null;
        }
    });

    // Before entering next page
    barba.hooks.beforeEnter((data) => {
        const nextHtml = data?.next?.html || '';
        updateBodyClasses(nextHtml);
        initBodyScript('techniconcept-projects-script');
        reloadJonnittoVideoScripts();

        document.querySelector('.techniconcept-loader.form-loader')?.remove();
    });

    // After DOM swap: finalize init and fade in
    barba.hooks.after((data) => {
        // Clear logo timeout and hide logo for back navigation
        clearTimeout(showLogoTimeout);
        const navigationType = sessionStorage.getItem('tc-navigation-type');
        if (navigationType === 'back') {
            transitionLogo.classList.add('hidden');
        }

        // Marquer la fin de la transition
        isTransitionInProgress = false;

        setTimeout(() => {
            firstPageLoad = false;
            updateHeadTag(data);
            updateHtmlTag(data);

            const hash = getHashFromTriggerOrLocation(data);
            if (hash) {
                const newUrl = `${window.location.pathname}${window.location.search}${hash}`;
                const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
                if (newUrl !== currentUrl) {
                    history.replaceState({}, '', newUrl);
                }
            }

            document.documentElement.classList.remove('mega-menu-opened');

            initPageScripts()
                .then(() => {
                    if (hash && tcNeosElementsBootstrap && tcNeosElementsBootstrap.anchor) {
                        tcNeosElementsBootstrap.anchor();
                    }

                    // Reinitialize Alpine.js components for video players
                    reinitializeAlpineComponents();

                    // Trigger GTM Page view event for Barba transitions
                    triggerGTMPageView();

                    document.body.classList.remove('no-scroll');

                    // Restore original body background
                    document.body.style.backgroundColor = '';

                    const container = document.querySelector('[data-barba="container"]');
                    if (container) {
                        // Handle scroll position BEFORE making content visible
                        handleScrollPosition();

                        // Make container visible and fade in
                        gsap.set(container, {visibility: 'visible'});
                        gsap.to(container, {opacity: 1, duration: 0.8});
                    } else {
                        console.warn('No container found after transition');
                    }

                    // Re-run viewport prefetch for new links
                    rebindPrefetchers();

                    return null;
                })
                .catch((error) => {
                    console.error('Error during initPageScripts:', error);
                    // Restore original body background even on error
                    document.body.style.backgroundColor = '';

                    // Handle scroll position even on error (before any content becomes visible)
                    handleScrollPosition();
                });
        }, 120);
    });

    // First load fade
    if (firstPageLoad) {
        const container = document.querySelector('[data-barba="container"]');
        if (container) {
            gsap.to(container, {opacity: 1, duration: 0.8});
        }
    }

    // Custom navigation event
    if (!window.__tcGoToProjectsBound__) {
        window.addEventListener('go-to-projects', (event) => {
            if (event?.detail?.url) barba.go(event.detail.url);
        });
        window.__tcGoToProjectsBound__ = true;
    }
};

export default {init};
